import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

/*
  CoreOrb V2 — Wireframe Mesh
  Glowing electric blue wireframe sphere with organic pulsing
*/

export default function CoreOrb({ size = 280, speaking = false, listening = false, thinking = false, error = false }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const w = size, h = size;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    camera.position.z = 3.8;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.style.background = 'transparent';
    mountRef.current?.appendChild(renderer.domElement);

    const geo = new THREE.IcosahedronGeometry(1, 20);
    const posAttr = geo.attributes.position;
    const origPos = new Float32Array(posAttr.array.length);
    origPos.set(posAttr.array);

    const wireMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color("#D9DBDD") },
        uGlow: { value: new THREE.Color("#FFFFFF") },
        uOpacity: { value: 0.7 },
      },
      vertexShader: `
        varying vec3 vPos;
        varying vec3 vNormal;
        void main() {
          vPos = position;
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform vec3 uGlow;
        uniform float uOpacity;
        varying vec3 vPos;
        varying vec3 vNormal;
        void main() {
          vec3 viewDir = normalize(cameraPosition - vPos);
          float fresnel = pow(1.0 - abs(dot(viewDir, vNormal)), 2.0);
          vec3 col = mix(uColor, uGlow, fresnel * 0.6);
          gl_FragColor = vec4(col, uOpacity + fresnel * 0.3);
        }
      `,
      transparent: true,
      wireframe: true,
      depthWrite: false,
    });

    const sphere = new THREE.Mesh(geo, wireMat);
    scene.add(sphere);

    const innerGeo = new THREE.IcosahedronGeometry(0.92, 16);
    const innerMat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color("#D9DBDD") },
        uTime: { value: 0 },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPos;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPos = (modelViewMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        varying vec3 vNormal;
        varying vec3 vPos;
        void main() {
          vec3 viewDir = normalize(-vPos);
          float fresnel = pow(1.0 - abs(dot(viewDir, vNormal)), 3.0);
          gl_FragColor = vec4(uColor * 0.2, 0.01 + fresnel * 0.04);
        }
      `,
      transparent: true,
      depthWrite: false,
    });
    const inner = new THREE.Mesh(innerGeo, innerMat);
    scene.add(inner);

    // Glow removed — wireframe looks clean on dark bg without it

    function noise3D(x, y, z) {
      const p = x * 7.13 + y * 11.71 + z * 17.23;
      return Math.sin(p) * Math.cos(p * 0.7 + 1.3) * 2;
    }

    let frame = 0;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      const t = performance.now() * 0.001;

      const pos = geo.attributes.position;
      const speed = 0.4;
      const strength = 0.06 + Math.sin(t * 0.5) * 0.02;

      for (let i = 0; i < pos.count; i++) {
        const ix = i * 3;
        const ox = origPos[ix], oy = origPos[ix + 1], oz = origPos[ix + 2];
        const n = noise3D(ox * 2 + t * speed, oy * 2 + t * speed * 0.7, oz * 2 + t * speed * 0.5);
        const disp = 1 + n * strength;
        pos.array[ix] = ox * disp;
        pos.array[ix + 1] = oy * disp;
        pos.array[ix + 2] = oz * disp;
      }
      pos.needsUpdate = true;
      geo.computeVertexNormals();

      const breathe = 1.0 + Math.sin(t * 0.7) * 0.03;
      sphere.scale.set(breathe, breathe, breathe);
      inner.scale.set(breathe * 0.92, breathe * 0.92, breathe * 0.92);

      sphere.rotation.y = t * 0.1;
      sphere.rotation.x = Math.sin(t * 0.15) * 0.15;
      inner.rotation.y = t * 0.08;
      inner.rotation.x = Math.sin(t * 0.12) * 0.1;

      wireMat.uniforms.uTime.value = t;
      innerMat.uniforms.uTime.value = t;

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frame);
      renderer.dispose();
      geo.dispose();
      wireMat.dispose();
      innerGeo.dispose();
      innerMat.dispose();
      if (mountRef.current?.contains(renderer.domElement)) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [size]);

  return (
    <div ref={mountRef} style={{
      width: size, height: size,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "transparent",
    }}><style>{`canvas { background: transparent !important; }`}</style></div>
  );
}
